import { create } from "@bufbuild/protobuf";
import { useMutation, UseMutationOptions } from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";

import { getArtifactDownloadUrl } from "@gx/ts-contracts/services/ontology/api/artifact-ArtifactService_connectquery";
import { GetProjectRequestSchema } from "@gx/ts-contracts/services/ontology/api/project_pb";
import { triggerOntologyProjectSyncWorkflow } from "@gx/ts-contracts/services/ontology/api/temporal-TemporalService_connectquery";

import {
  createGetProjectQueryKey,
  createListProjectsInfiniteQueryKey,
} from "@/api/queries/projects";

// ========== RUN ONTLOGY PIPELINE ==========

export const useTriggerOntologyProjectSyncWorkflowMutation = (
  options?: UseMutationOptions<
    typeof triggerOntologyProjectSyncWorkflow.input,
    typeof triggerOntologyProjectSyncWorkflow.output
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation<
    typeof triggerOntologyProjectSyncWorkflow.input,
    typeof triggerOntologyProjectSyncWorkflow.output
  >(triggerOntologyProjectSyncWorkflow, {
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      const projectId = variables.input?.projectId;
      if (projectId) {
        const projectQueryKey = createGetProjectQueryKey(
          create(GetProjectRequestSchema, { projectId }),
        );
        const invalidateProject = () =>
          void queryClient.invalidateQueries({ queryKey: projectQueryKey });

        invalidateProject();
        // Temporal syncs are async; schedule a few follow-up invalidations
        // to catch the new version once it materializes.
        setTimeout(invalidateProject, 1000);
        setTimeout(invalidateProject, 3000);
        setTimeout(invalidateProject, 6000);
      }
      void queryClient.invalidateQueries({
        queryKey: createListProjectsInfiniteQueryKey(),
      });

      if (options?.onSuccess) {
        options.onSuccess(data, variables, onMutateResult, context);
      }
    },
  });
};

// ========== GET ARTIFACT DOWNLOAD URL ==========

export const useGetArtifactDownloadUrlMutation = (
  options?: UseMutationOptions<
    typeof getArtifactDownloadUrl.input,
    typeof getArtifactDownloadUrl.output
  >,
) => {
  return useMutation<typeof getArtifactDownloadUrl.input, typeof getArtifactDownloadUrl.output>(
    getArtifactDownloadUrl,
    options,
  );
};
